const CART_DRAWER_CONFETTI_FALLBACK_SRC =
  'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
const CART_DRAWER_CONFETTI_DEFAULTS = {
  spread: 360,
  ticks: 100,
  gravity: 0,
  decay: 0.94,
  startVelocity: 30,
};
const CART_DRAWER_CONFETTI_COLORS = ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'];
const CART_DRAWER_CONFETTI_COOLDOWN = 4000;
const CART_DRAWER_CONFETTI_ORIGIN = { x: 0.5, y: 1.12 };

function loadCartDrawerConfettiLibrary(preferredSrc) {
  if (window.cartDrawerConfettiLibraryPromise) {
    return window.cartDrawerConfettiLibraryPromise;
  }

  if (window.confetti) {
    window.cartDrawerConfettiLibraryPromise = Promise.resolve(window.confetti);
    return window.cartDrawerConfettiLibraryPromise;
  }

  const scriptSrc = preferredSrc || CART_DRAWER_CONFETTI_FALLBACK_SRC;

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.setAttribute('data-cart-drawer-confetti', 'true');
      script.onload = () => resolve(window.confetti);
      script.onerror = (error) => {
        script.remove();
        reject(error);
      };
      document.head.appendChild(script);
    });

  window.cartDrawerConfettiLibraryPromise = loadScript(scriptSrc).catch((error) => {
    if (!preferredSrc || preferredSrc === CART_DRAWER_CONFETTI_FALLBACK_SRC) {
      console.error('[CartDrawer] Failed to load confetti library', error);
      throw error;
    }

    console.warn('[CartDrawer] Confetti asset failed, falling back to CDN', {
      preferredSrc,
      error,
    });
    return loadScript(CART_DRAWER_CONFETTI_FALLBACK_SRC).catch((fallbackError) => {
      console.error('[CartDrawer] Fallback confetti library failed to load', fallbackError);
      throw fallbackError;
    });
  });

  return window.cartDrawerConfettiLibraryPromise;
}

function ensureCartDrawerConfettiInstance(canvas, preferredSrc) {
  if (!canvas) return Promise.resolve(null);

  return loadCartDrawerConfettiLibrary(preferredSrc)
    .then((confettiLib) => {
      if (!confettiLib) return null;
      if (canvas.confetti) return canvas.confetti;

      const instance = confettiLib.create(canvas, {
        resize: true,
        useWorker: false,
      });

      if (instance && typeof instance.then === 'function') {
        return instance.then((result) => {
          canvas.confetti = result;
          return canvas.confetti;
        });
      }

      canvas.confetti = instance;
      return canvas.confetti;
    })
    .catch((error) => {
      console.error('[CartDrawer] Unable to initialize confetti', error);
      return null;
    });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.confettiSrc = this.dataset.confettiSrc;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.classList.contains('active')) {
        this.close();
      } else {
        this.open(cartLink);
      }
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.maybeCelebrateFreeShipping();
    this.observeFreeShippingUnlock();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  maybeCelebrateFreeShipping() {
    const banner = this.querySelector('.cart__free-shipping');
    if (!banner) return;

    const status = banner.getAttribute('data-free-shipping-status');
    if (status !== 'unlocked') return;

    const now = Date.now();
    if (this._lastConfettiRun && now - this._lastConfettiRun < CART_DRAWER_CONFETTI_COOLDOWN) {
      return;
    }
    const canvas = this.querySelector('#cart-drawer-confetti');
    if (!canvas) return;

    if (this._confettiPending) return;
    this._confettiPending = true;

    ensureCartDrawerConfettiInstance(canvas, this.confettiSrc)
      .then((confettiInstance) => {
        if (typeof confettiInstance !== 'function') return;
        this._lastConfettiRun = Date.now();

        confettiInstance({ spread: 70, origin: CART_DRAWER_CONFETTI_ORIGIN });

        const defaults = Object.assign({}, CART_DRAWER_CONFETTI_DEFAULTS, {
          origin: CART_DRAWER_CONFETTI_ORIGIN,
        });

        const shoot = () => {
          const totalParticles = 200;
          const fire = (particleRatio, options) => {
            confettiInstance(
              Object.assign({}, defaults, options, {
                particleCount: Math.floor(totalParticles * particleRatio),
              })
            );
          };

          fire(0.25, {
            spread: 26,
            startVelocity: 55,
            colors: CART_DRAWER_CONFETTI_COLORS,
            shapes: ['circle', 'square'],
          });

          fire(0.2, {
            spread: 60,
            colors: CART_DRAWER_CONFETTI_COLORS,
            shapes: ['circle', 'square'],
          });

          fire(0.35, {
            spread: 100,
            decay: 0.91,
            scalar: 0.8,
            colors: CART_DRAWER_CONFETTI_COLORS,
            shapes: ['circle', 'square'],
          });

          fire(0.1, {
            spread: 120,
            startVelocity: 25,
            decay: 0.92,
            scalar: 1.2,
            colors: CART_DRAWER_CONFETTI_COLORS,
            shapes: ['circle', 'square'],
          });

          fire(0.1, {
            spread: 120,
            startVelocity: 45,
            colors: CART_DRAWER_CONFETTI_COLORS,
            shapes: ['circle', 'square'],
          });

          confettiInstance(
            Object.assign({}, defaults, {
              particleCount: 30,
              scalar: 1.2,
              shapes: ['circle', 'square'],
              colors: CART_DRAWER_CONFETTI_COLORS,
            })
          );

          confettiInstance(
            Object.assign({}, defaults, {
              particleCount: 20,
              scalar: 2,
              shapes: ['emoji'],
              shapeOptions: {
                emoji: {
                  value: ['🦄', '🌈'],
                },
              },
            })
          );
        };

        setTimeout(shoot, 0);
        setTimeout(shoot, 100);
        setTimeout(shoot, 200);
      })
      .catch(() => {})
      .then(() => {
        this._confettiPending = false;
      });
  }

  observeFreeShippingUnlock() {
    if (this._freeShipObserver) {
      try { this._freeShipObserver.disconnect(); } catch (_) {}
    }

    const target = this; // observe the whole drawer to catch any banner re-render
    if (!target) return;

    let lastStatus = (this.querySelector('.cart__free-shipping') || {}).getAttribute?.('data-free-shipping-status') || '';

    const observer = new MutationObserver(() => {
      if (!this.classList.contains('active')) return;
      const banner = this.querySelector('.cart__free-shipping');
      if (!banner) return;
      const status = banner.getAttribute('data-free-shipping-status');
      if (status === 'unlocked' && lastStatus !== 'unlocked') {
        this.maybeCelebrateFreeShipping();
      }
      lastStatus = status;
    });

    observer.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-free-shipping-status'] });
    this._freeShipObserver = observer;
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.observeFreeShippingUnlock();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
